import BaseAction from '../../components/base/base-action';
import settingsRequests from './settings-requests';
import { defaultLocale } from './default-locale';
import { moduleId } from '../../../build/get-module-id';
import _ from 'lodash';
import { APP_PUBLIC_PLAYER_HOST_ID } from './settings-constants';
import {
    IActiveUserPermissionsData,
    IEnvironmentSettingsState,
    ISettingsStoreSourcesStateUnparsedBackend,
    ISettingsStoreSourcesStateUnparsedFile,
    ISettingsStoreState,
    ISystemSettings
} from './settings-types';
import settingsStore from './settings-store';
// tslint:disable-next-line
const fetchPermission = require('../../bootstrap/updated/public-player-customize-helper').fetchPermission();

function mergeSettings(
    settingsRes: ISettingsStoreSourcesStateUnparsedBackend,
    defaultSettingsRes: ISettingsStoreSourcesStateUnparsedFile,
    customSettingsRes: ISettingsStoreSourcesStateUnparsedFile
): ISystemSettings {
    return _.assign(
        {},
        defaultSettingsRes.settings || {},
        settingsRes.system || {},
        customSettingsRes.settings || customSettingsRes.system || {}
    );
}

function mergeI18n(
    settingsRes: ISettingsStoreSourcesStateUnparsedBackend,
    defaultSettingsRes: ISettingsStoreSourcesStateUnparsedFile,
    customSettingsRes: ISettingsStoreSourcesStateUnparsedFile
): { [messageId: string]: string } {
    const locale =
        (settingsRes.environment || ({} as IEnvironmentSettingsState)).locale || defaultLocale;
    return _.assign(
        {},
        (defaultSettingsRes.i18n || {})[locale] || {},
        settingsRes.i18n || {},
        (customSettingsRes.i18n || {})[locale] || {}
    );
}

class SettingsActions extends BaseAction {
    public dispatchEventName: string = 'setSettingsStore';

    public async getSettings(sources: string[], host: string = '', lang?: string, jwt?: string): Promise<void> {
        const processedSources = _.map(sources, source => {
            return source !== 'system' ? source : moduleId;
        });
        const settingsRes: ISettingsStoreSourcesStateUnparsedBackend = await this.startProcessing(
            settingsRequests.fetchSettings(processedSources, host, lang, jwt)
        );
        if (settingsRes.i18n) {
            const i18n = {};
            const msgKeys = _.keys(settingsRes.i18n);
            const prefix = 'js.' + moduleId + '.';
            _.forEach(msgKeys, msgKey => {
                if (msgKey.indexOf(prefix) === 0) {
                    i18n[msgKey.substring(prefix.length)] = settingsRes.i18n[msgKey];
                } else {
                    console.warn('Expect localization string key to start with prefix', msgKey);
                }
            });
            settingsRes.i18n = i18n;
        }
        if (settingsRes[moduleId]) {
            settingsRes.system = settingsRes[moduleId];
            delete settingsRes[moduleId];
        }
        if (fetchPermission) {
            try {
                const permissionsRes: IActiveUserPermissionsData = await this.startProcessing(
                    settingsRequests.fetchPermissions(jwt)
                );
                if (settingsRes && settingsRes.environment && settingsRes.environment.userProfile) {
                    settingsRes.environment.userProfile.permissions = permissionsRes.permissions;
                    if (permissionsRes.id !== undefined) {
                        settingsRes.environment.userProfile.id = permissionsRes.id;
                    }
                } else {
                    console.log('Cannot get userProfile field from settings!', settingsRes);
                }
            } catch (error) {
                // cannot get permissions
            }
        }
        const defaultSettingsRes = await this.startProcessing(
            settingsRequests.fetchDefaultSettings(host)
        );
        let customSettingsRes = {};
        try {
            customSettingsRes = await this.startProcessing(
                settingsRequests.fetchCustomSettings(host)
            );
        } catch (error) {
            // it is normal not to have custom settings
        }

        settingsRes.system = mergeSettings(settingsRes, defaultSettingsRes, customSettingsRes);
        if (settingsRes.i18n) {
            settingsRes.i18n = mergeI18n(settingsRes, defaultSettingsRes, customSettingsRes);
        }
        this.dispatch({
            sources: settingsRes,
            loaded: true
        });
    }

    public async getSettingsFromFilesOnly(
        host: string
    ): Promise<ISettingsStoreSourcesStateUnparsedBackend> {
        const defaultSettingsRes = await this.startProcessing(
            settingsRequests.fetchDefaultSettings(host)
        );
        let customSettingsRes = {};
        try {
            customSettingsRes = await this.startProcessing(
                settingsRequests.fetchCustomSettings(host)
            );
        } catch (error) {
            // it is normal not to have custom settings
        }
        const settings: ISettingsStoreSourcesStateUnparsedBackend = {};
        settings.system = mergeSettings(settings, defaultSettingsRes, customSettingsRes);
        settings.i18n = mergeI18n(settings, defaultSettingsRes, customSettingsRes);

        this.dispatch({
            sources: settings,
            loaded: true
        });
        return settings;
    }

    public setPublicPlayerHost(store: ISettingsStoreState): void {
        store.publicPlayerHost = (window[APP_PUBLIC_PLAYER_HOST_ID] as string) || '';
        this.dispatch(store);
    }

    public setLoadingState(state: boolean): void {
        const settings = settingsStore.getState();
        this.dispatch({
            ...settings,
            loaded: state
        });
    }
}

const actions = new SettingsActions();

// tslint:disable-next-line:no-default-export (autogenerated)
export default actions;
